import { useState } from 'react';
import type { ReactNode, ButtonHTMLAttributes } from 'react';
import type { LeaderboardRow } from '@quiz/shared';

export function Screen({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`min-h-dvh bg-ink text-chalk flex flex-col ${className}`}>
      <div className="mx-auto w-full max-w-lg flex-1 flex flex-col px-4 py-5">{children}</div>
    </div>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger' | 'go';
};

/** Big tap targets: min-h-14 everywhere, because this is used one-handed. */
export function Button({ variant = 'primary', className = '', ...props }: ButtonProps) {
  const styles = {
    primary: 'bg-sky text-ink hover:brightness-110',
    go: 'bg-go text-ink hover:brightness-110',
    danger: 'bg-stop text-white hover:brightness-110',
    ghost: 'bg-ink-3 text-chalk hover:bg-ink-3/80',
  }[variant];

  return (
    <button
      {...props}
      className={`min-h-14 w-full rounded-xl px-5 text-lg font-semibold transition
        disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.99] ${styles} ${className}`}
    />
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-2xl bg-ink-2 p-5 ${className}`}>{children}</div>;
}

export function Banner({ kind, children }: { kind: 'error' | 'info'; children: ReactNode }) {
  const tone = kind === 'error' ? 'bg-stop/15 text-stop' : 'bg-sky/15 text-sky';
  return <div className={`rounded-xl px-4 py-3 text-sm ${tone}`} role="status">{children}</div>;
}

/**
 * The countdown bar. Rendered from endsAt minus the server-clock offset, so a
 * phone with the wrong time still shows the right number. Display only.
 */
export function Countdown({ msLeft, totalMs }: { msLeft: number; totalMs: number }) {
  const seconds = Math.ceil(msLeft / 1000);
  const fraction = totalMs > 0 ? Math.max(0, Math.min(1, msLeft / totalMs)) : 0;
  const urgent = seconds <= 5;

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wide text-muted">Time left</span>
        <span
          className={`tabular-nums text-2xl font-bold ${urgent ? 'text-stop' : 'text-chalk'}`}
          aria-live={urgent ? 'assertive' : 'off'}
        >
          {seconds}s
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-ink-3">
        <div
          className={`h-full rounded-full transition-[width] duration-100 ease-linear ${urgent ? 'bg-stop' : 'bg-go'}`}
          style={{ width: `${fraction * 100}%` }}
        />
      </div>
    </div>
  );
}

export function Leaderboard({
  rows,
  highlightPlayerId,
  title = 'Standings',
}: {
  rows: LeaderboardRow[];
  highlightPlayerId?: string;
  title?: string;
}) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <h2 className="mb-3 text-xs uppercase tracking-wide text-muted">{title}</h2>
      <ol className="space-y-1">
        {rows.map((row) => {
          const isYou = row.playerId === highlightPlayerId;
          return (
            <li
              key={row.playerId}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isYou ? 'bg-sky/20' : ''}`}
            >
              <span className="w-6 tabular-nums text-muted">{row.rank}</span>
              <span className="flex-1 truncate font-medium">
                {row.callSign}
                {isYou && <span className="ml-2 text-xs text-sky">you</span>}
              </span>
              <span className="tabular-nums font-bold">{row.score}</span>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex flex-1 items-center justify-center text-muted" role="status">
      <span className="animate-pulse">{label}…</span>
    </div>
  );
}

/**
 * The venue's mark, at the top of the two screens a player sits looking at:
 * the home page and the holding area.
 *
 * The source artwork is a 4305x2477 JPEG, white on solid black, with the logo
 * occupying 55% of the canvas height. Dropped in as-is it would be a 377kB
 * black rectangle rendering a postage-stamp logo adrift in its own padding.
 * So `public/tpof-logo.png` is a prepared asset, not the original: cropped to
 * its content, then with its luminance promoted to an alpha channel, so the
 * white artwork stayed opaque and the black field became transparent. That is
 * why this needs no blend mode -- it sits correctly on any background -- and
 * why it is 30kB rather than 377kB. Regenerating it from a new original means
 * doing the same two things.
 *
 * It removes itself if the file is missing rather than leaving a broken image
 * on somebody's phone. The app has to work on a fresh deployment where nobody
 * has dropped a logo in yet, and a torn-page icon in a dark pub reads as "this
 * thing is broken".
 */
export function Logo({ className = 'h-16' }: { className?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  return (
    <img
      src="/tpof-logo.png"
      alt="The Price of Freedom"
      onError={() => setFailed(true)}
      className={`mx-auto w-auto object-contain ${className}`}
    />
  );
}

/** The crowdfunder this quiz is raising interest for. */
export const CAMPAIGN_URL = 'https://greenlit.com/project/price-freedom-star-wars-story';

/**
 * The ask.
 *
 * Placed on the two screens where somebody is not mid-question: the welcome
 * page while they wait for the host, and the final standings once it is over.
 * Deliberately nowhere else -- a fundraising banner over a live question is the
 * fastest way to make people stop reading the app.
 *
 * It opens in a new tab on purpose. A player tapping this mid-evening must come
 * back to a quiz that is still where they left it, and on a phone that means
 * not navigating the quiz away at all.
 */
export function SupportCta({ className = '' }: { className?: string }) {
  return (
    <Card className={`text-center ${className}`}>
      <p className="text-sm text-muted">
        Thank you for supporting us, and our vision to bring Star Wars fans closer to creating
        what we love.
      </p>
      <p className="mt-2 text-sm text-muted">
        A small contribution to our crowdfunder would mean the world to us, and help elevate
        this great film into a legendary one.
      </p>
      <a
        href={CAMPAIGN_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 flex min-h-14 w-full items-center justify-center rounded-xl bg-flag px-5 text-lg font-semibold text-ink transition hover:brightness-110 active:scale-[0.99]"
      >
        Back the film
      </a>
    </Card>
  );
}
